import React from 'react';
import { Info, Tag, Calendar, Clock, Star, Award, History, RotateCcw, CheckCircle } from 'lucide-react';

const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    });
};

const TaskInfoModal = ({
    isOpen,
    onClose,
    noteCategory,
    createdAt,
    taskType,
    scheduledFor,
    dueAt,
    metrics,
    assignedDays
}) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl w-[32rem] max-w-[90vw]">
                <h2 className="text-xl flex font-bold mb-6 border-b pb-3">
                    <Info size={24} className="mr-2" />
                    Task Information
                </h2>

                <div className="space-y-4">
                    {/* Category */}
                    <div className="flex items-center">
                        <Tag className="w-5 h-5 mr-3 text-purple-600" />
                        <div>
                            <div className="text-sm text-gray-500">Category</div>
                            <div className="font-medium">{noteCategory}</div>
                        </div>
                    </div>

                    {/* Created Date */}
                    <div className="flex items-center">
                        <History className="w-5 h-5 mr-3 text-purple-600" />
                        <div>
                            <div className="text-sm text-gray-500">Created</div>
                            <div className="font-medium">{formatDateTime(createdAt)}</div>
                        </div>
                    </div>

                    {/* Schedule Date - Only for scheduled tasks */}
                    {taskType === 'scheduled' && scheduledFor && (
                        <div className="flex items-center">
                            <Clock className="w-5 h-5 mr-3 text-purple-600" />
                            <div>
                                <div className="text-sm text-gray-500">Scheduled For</div>
                                <div className="font-medium">{formatDateTime(scheduledFor)}</div>
                            </div>
                        </div>
                    )}

                    {/* Due Date - Only for active and scheduled tasks */}
                    {(taskType === 'active' || taskType === 'scheduled') && dueAt && (
                        <div className="flex items-center">
                            <Calendar className="w-5 h-5 mr-3 text-purple-600" />
                            <div>
                                <div className="text-sm text-gray-500">Due At</div>
                                <div className="font-medium">{formatDateTime(dueAt)}</div>
                            </div>
                        </div>
                    )}

                    {/* Recurring Task Information */}
                    {taskType === 'recurring' && (
                        <>
                            <div className="border-t my-4"></div>

                            {/* Completion Stats */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="flex items-center">
                                    <CheckCircle className="w-5 h-5 mr-3 text-purple-600" />
                                    <div>
                                        <div className="text-sm text-gray-500">Total Completions</div>
                                        <div className="font-medium">{metrics.totalCompletions}</div>
                                    </div>
                                </div>

                                <div className="flex items-center">
                                    <Star className="w-5 h-5 mr-3 text-purple-600" />
                                    <div>
                                        <div className="text-sm text-gray-500">Current Streak</div>
                                        <div className="font-medium">{metrics.currentStreak} days</div>
                                    </div>
                                </div>

                                <div className="flex items-center">
                                    <Award className="w-5 h-5 mr-3 text-purple-600" />
                                    <div>
                                        <div className="text-sm text-gray-500">Longest Streak</div>
                                        <div className="font-medium">{metrics.longestStreak} days</div>
                                    </div>
                                </div>

                                <div className="flex items-center">
                                    <RotateCcw className="w-5 h-5 mr-3 text-purple-600" />
                                    <div>
                                        <div className="text-sm text-gray-500">Recurring Days</div>
                                        <div className="font-medium">
                                            {assignedDays.length === 7
                                                ? 'Every day'
                                                : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
                                                    .filter((_, i) => assignedDays.includes(i))
                                                    .join(', ')}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {metrics.lastCompletedAt && (
                                <div className="flex items-center mt-4">
                                    <History className="w-5 h-5 mr-3 text-purple-600" />
                                    <div>
                                        <div className="text-sm text-gray-500">Last Completed</div>
                                        <div className="font-medium">
                                            {formatDateTime(metrics.lastCompletedAt)}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                <button
                    className="mt-6 w-full px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded hover:opacity-90 transition-opacity"
                    onClick={onClose}
                >
                    Close
                </button>
            </div>
        </div>
    );
};

export default TaskInfoModal;